import React, {useCallback, useEffect, useRef} from 'react';
import {Dialog, DialogBackdrop, DialogPanel, DialogTitle} from "@headlessui/react";
import {FaTimes} from "react-icons/fa";
import ModalBody from "./modal-body.jsx";
import ModalFooter from "./modal-footer.jsx";
import ReportUtils from "../../utils/report-utils.js";
import {isEnabledRequiredField} from "../../utils/project-utils.js";

let lastClosedModalId = null;
const openModalIds = new Set();
const REQUIRED_FIELD_SELECTOR = "[data-required-field='true'], input[required], textarea[required], select[required]";

const hasEnabledRequiredFields = (element) => [...element.querySelectorAll(REQUIRED_FIELD_SELECTOR)].some(isEnabledRequiredField);

const ModalComponent = ({open, setOpen, title, children, className = "w-10/12", headerClass = "", onClick, overflow = false, isCloseClick = true, showCloseButton = true, onShow, alignTop = false, animated = true}) => {
    const titleTooltip = typeof title === "string" || typeof title === "number" ? String(title) : undefined;
    const modalId = useRef(Symbol("modal"));
    const wasOpen = useRef(false);
    const panelRef = useRef(null);
    const hasRequiredFields = useRef(false);

    const setPanelRef = useCallback((node) => {
        panelRef.current = node;

        if (node) hasRequiredFields.current = hasEnabledRequiredFields(node);
    }, []);

    useEffect(() => {
        if (open && onShow) onShow()
    }, [open]);

    useEffect(() => {
        if (open && !wasOpen.current) {
            lastClosedModalId = null;
            openModalIds.add(modalId.current);
        }

        if (wasOpen.current && !open) {
            openModalIds.delete(modalId.current);
            lastClosedModalId = hasRequiredFields.current ? modalId.current : null;
        }

        wasOpen.current = open;
    }, [open]);

    useEffect(() => () => openModalIds.delete(modalId.current), []);

    useEffect(() => {
        if (!open || !panelRef.current) return;

        const updateRequiredFields = () => {
            hasRequiredFields.current = hasEnabledRequiredFields(panelRef.current);
        };
        const observer = new MutationObserver(updateRequiredFields);

        updateRequiredFields();
        observer.observe(panelRef.current, {attributes: true, childList: true, subtree: true});

        return () => observer.disconnect();
    }, [open]);

    useEffect(() => {
        const reopenAfterRequiredError = () => {
            if (openModalIds.size || lastClosedModalId !== modalId.current) return;

            lastClosedModalId = null;
            setOpen(true);
        };

        window.addEventListener("required-fields-error", reopenAfterRequiredError);
        return () => window.removeEventListener("required-fields-error", reopenAfterRequiredError);
    }, [setOpen]);

    return (
        <Dialog open={open} onClose={isCloseClick ? setOpen : () => {}} className="relative z-[1010]">
            <DialogBackdrop
                transition={animated}
                className="fixed inset-0 bg-black opacity-50 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
            />

            <div className={"fixed inset-0 p-1 w-full overflow-y-auto justify-self-center report-center"} onClick={onClick}>
                <div className={`flex h-full items-end justify-center text-center sm:p-0 ${alignTop ? "sm:items-start" : "sm:items-center"}`}>
                    <DialogPanel
                        transition={animated}
                        className={[`relative transform ${overflow ? 'overflow-visible' : 'overflow-hidden'} rounded-xl bg-white text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95`, className].join(" ")}
                    >
                        <div ref={setPanelRef} className="bg-white border-b border-gray-300 rounded-xl">
                            {
                                (title || showCloseButton ) &&
                                <DialogTitle as="h3" className={["font-semibold text-gray-500 leading-6 border-b border-gray-300 px-3 py-3", "flex items-center justify-between !my-0", headerClass].join(" ")}>
                                    <span className={'min-w-0 flex-1 pr-2 break-all!'} title={titleTooltip}>{ReportUtils.textLimit(title, 150)}</span>

                                    {
                                        showCloseButton &&
                                        <span onClick={() => setOpen(false)} className="cursor-pointer flex shrink-0 items-center hover:text-gray-700">
                                            <FaTimes/>
                                        </span>
                                    }
                                </DialogTitle>
                            }

                            {children}
                        </div>
                    </DialogPanel>
                </div>
            </div>
        </Dialog>
    );
};

export const Modal = Object.assign(ModalComponent, {
    Body: ModalBody,
    Footer: ModalFooter
});